import { PageLoadingIndicator } from "@ecologi/react-components/src/PageLoadingIndicator";
import { PlatformFeature } from "@ecologi/shared";
import { FC } from "react";
import { useFeatureFlagGuard } from "./use-feature-flag-guard";

type LayoutProps = {
  isProfile?: boolean;
  isLightHeader?: boolean;
  isWhiteBody?: boolean;
};

/**
 * This HOC switches out the supplied component with a replacement component, depending
 * on the state of the supplied feature flags in ConfigCat.
 *
 * Note: the supplied component will re-render if the current user changes.
 *
 * That normally won't be an issue, but it's useful to be aware of this behaviour on
 * certain pages (e.g. signup, where a user suddenly exists after being undefined; also
 * the profile page, if a user switches from someone else's profile to their own).
 *
 * One exception: if solely `PlatformFeature.Maintenance` is passed, the component will
 * not re-render. That's because in this situation we specifically avoid passing the user
 * object. (The assumption being: there's no need to filter by user, as maintenance
 * affects everyone.
 */
export function withFeatureFlagGuard<T>(
  Component: FC<T>,
  HoldingPageComponent: FC<T>,
  allowIfFeatureIs: Partial<Record<PlatformFeature, boolean>>
) {
  const wrappedComponent = (props: T) => {
    const { isAllowed, isLoading } = useFeatureFlagGuard(allowIfFeatureIs);

    if (isLoading) return <PageLoadingIndicator />;
    if (isAllowed) return <Component {...props} />;

    return <HoldingPageComponent {...props} />;
  };

  wrappedComponent.layoutProps = {} as LayoutProps;

  return wrappedComponent;
}
